import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import {
  AuditCase,
  AuditProgram,
  AuditSourceAdapter,
  AuditSourceResource,
  AuditTemplate,
  AuditTemplateVersion,
  SelectOption
} from '../audit.models';
import { AuditService } from '../audit.service';
import { auditNavItems, auditTypeOptions, moduleOptions, riskOptions, scopeTypeOptions } from '../audit-ui';

@Component({
  selector: 'app-audit-case-create',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    @if (loading || saving) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-shell>
      <app-page-hero eyebrow="Audit Execution" title="Create Audit Case" subtitle="Start a manual audit from a published checklist template.">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" [outlined]="true" severity="secondary"></a>
          }
        </div>
        <div actions>
          <a pButton routerLink="/audit/cases" label="Cases" icon="pi pi-arrow-left" [outlined]="true" severity="secondary"></a>
          <p-button label="Create Case" icon="pi pi-check" (onClick)="createCase()" [loading]="saving" [disabled]="!canCreate"></p-button>
        </div>
      </app-page-hero>

      <app-page-data-panel title="Case Setup" subtitle="Template, ownership, source reference, and audit scope.">
        <div class="audit-form-grid">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-list-check"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="case-template" [options]="templateOptions" [(ngModel)]="selectedTemplateId" optionLabel="label" optionValue="value" (onChange)="loadTemplateVersion()"></p-select>
              <label for="case-template">Template</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-sitemap"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="case-program" [options]="programOptions" [(ngModel)]="form.programId" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="case-program">Program</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-th-large"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="case-module" [options]="moduleOptions" [(ngModel)]="form.moduleCode" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="case-module">Module</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-verified"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="case-audit-type" [options]="auditTypeOptions" [(ngModel)]="form.auditType" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="case-audit-type">Audit type</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="case-risk" [options]="riskOptions" [(ngModel)]="form.riskLevel" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="case-risk">Risk level</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-crosshairs"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="case-scope-type" [options]="scopeTypeOptions" [(ngModel)]="form.scopeType" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="case-scope-type">Scope type</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-inputnumber inputId="case-scope-id" [(ngModel)]="form.scopeId" [useGrouping]="false"></p-inputnumber>
              <label for="case-scope-id">Scope ID</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-tag"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="case-scope-number" [(ngModel)]="form.scopeNumber">
              <label for="case-scope-number">Scope number</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-id-card"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="case-scope-display-name" [(ngModel)]="form.scopeDisplayName">
              <label for="case-scope-display-name">Scope display name</label>
            </p-iftalabel>
          </p-inputgroup>
        </div>
      </app-page-data-panel>

      <app-page-data-panel title="Source Reference" subtitle="Search LogiLink records or enter a manual source reference. No cross-module foreign keys are required.">
        <div class="source-picker">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-plug"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="case-source-adapter" [options]="sourceAdapterOptions" [(ngModel)]="selectedSourceAdapterKey" optionLabel="label" optionValue="value" [showClear]="true"></p-select>
              <label for="case-source-adapter">Source adapter</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-search"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="case-source-query" [(ngModel)]="sourceQuery">
              <label for="case-source-query">Search source records</label>
            </p-iftalabel>
          </p-inputgroup>
          <div class="source-picker-actions">
            <p-button label="Search" icon="pi pi-search" [outlined]="true" severity="secondary" (onClick)="searchSourceResources()" [disabled]="!selectedSourceAdapterKey || sourceSearching"></p-button>
          </div>
        </div>

        @if (sourceResults.length) {
          <p-table [value]="sourceResults" styleClass="p-datatable-sm source-table" responsiveLayout="scroll">
            <ng-template pTemplate="header">
              <tr>
                <th>Source</th>
                <th>Status</th>
                <th>Summary</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-source>
              <tr>
                <td>
                  <div class="audit-stack">
                    <strong>{{ source.sourceDisplayName || source.sourceResourceNumber || '-' }}</strong>
                    <small>{{ source.sourceModule }}/{{ source.sourceResourceType }} - {{ source.sourceResourceNumber || source.sourceResourceId }}</small>
                  </div>
                </td>
                <td>{{ source.status || '-' }}</td>
                <td>{{ source.summary || '-' }}</td>
                <td class="text-right">
                  <p-button label="Use" icon="pi pi-check" [outlined]="true" severity="secondary" (onClick)="useSourceResource(source)"></p-button>
                </td>
              </tr>
            </ng-template>
          </p-table>
        }

        <div class="audit-form-grid">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-th-large"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="case-source-module" [(ngModel)]="form.sourceModule">
              <label for="case-source-module">Source module</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-box"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="case-resource-type" [(ngModel)]="form.sourceResourceType">
              <label for="case-resource-type">Resource type</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-inputnumber inputId="case-resource-id" [(ngModel)]="form.sourceResourceId" [useGrouping]="false"></p-inputnumber>
              <label for="case-resource-id">Resource ID</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-tag"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="case-resource-number" [(ngModel)]="form.sourceResourceNumber">
              <label for="case-resource-number">Resource number</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-id-card"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="case-source-display-name-manual" [(ngModel)]="form.sourceDisplayName">
              <label for="case-source-display-name-manual">Source display name</label>
            </p-iftalabel>
          </p-inputgroup>
        </div>
      </app-page-data-panel>

      @if (formError) {
        <div class="audit-error">{{ formError }}</div>
      }
    </app-page-shell>
  `,
  styles: [`
    .audit-form-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(180px, 1fr));
      gap: .85rem;
    }

    label {
      color: #334155;
      display: grid;
      gap: .35rem;
      min-width: 0;
    }

    label span {
      color: #64748b;
      font-size: .82rem;
      font-weight: 700;
    }

    input,
    p-iftalabel,
    p-inputgroup,
    p-select,
    p-inputnumber {
      width: 100%;
    }

    .audit-error {
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      color: #991b1b;
      font-weight: 700;
      padding: .75rem;
    }

    .source-picker {
      align-items: end;
      display: grid;
      grid-template-columns: minmax(220px, .8fr) minmax(260px, 1fr) auto;
      gap: .85rem;
      margin-bottom: 1rem;
    }

    .source-picker-actions {
      display: flex;
      justify-content: flex-end;
    }

    .source-table {
      margin-bottom: 1rem;
    }

    .audit-stack {
      display: flex;
      flex-direction: column;
      gap: .2rem;
      min-width: 0;
    }

    .audit-stack small {
      color: #64748b;
      font-size: .78rem;
    }

    @media (max-width: 900px) {
      .audit-form-grid,
      .source-picker {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class AuditCaseCreateComponent implements OnInit {
  private readonly auditService = inject(AuditService);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  saving = false;
  formError = '';
  programs: AuditProgram[] = [];
  templates: AuditTemplate[] = [];
  sourceAdapters: AuditSourceAdapter[] = [];
  sourceResults: AuditSourceResource[] = [];
  selectedSourceAdapterKey?: string;
  sourceQuery = '';
  sourceSearching = false;
  selectedTemplateId?: number;
  selectedVersion?: AuditTemplateVersion;
  form: AuditCase = {};

  readonly moduleOptions = moduleOptions;
  readonly auditTypeOptions = auditTypeOptions;
  readonly scopeTypeOptions = scopeTypeOptions;
  readonly riskOptions = riskOptions;
  readonly visibleNavItems = auditNavItems.filter(item => this.permissionService.hasPermission(item.permission));
  readonly canCreate = this.permissionService.hasPermission(app_permissions.audit.cases.create);

  ngOnInit(): void {
    this.loading = true;
    forkJoin({
      programs: this.auditService.programs(),
      templates: this.auditService.templates(),
      sourceAdapters: this.auditService.sourceAdapters()
    }).subscribe({
      next: response => {
        this.programs = response.programs.data ?? [];
        this.templates = response.templates.data ?? [];
        this.sourceAdapters = response.sourceAdapters.data ?? [];
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Case setup', detail: 'Unable to load setup data.' }),
      complete: () => this.loading = false
    });
  }

  get templateOptions(): SelectOption<number>[] {
    return this.templates
      .filter(template => template.status === 'PUBLISHED')
      .map(template => ({ label: `${template.templateName} (${template.templateCode})`, value: template.auditTemplateId! }));
  }

  get programOptions(): SelectOption<number>[] {
    return this.programs.map(program => ({ label: `${program.programName} (${program.programCode})`, value: program.auditProgramId! }));
  }

  get sourceAdapterOptions(): SelectOption<string>[] {
    return this.sourceAdapters
      .filter(adapter => adapter.active && adapter.supportsSearch)
      .map(adapter => ({
        label: `${adapter.displayName || adapter.sourceResourceType} (${adapter.sourceModule}/${adapter.sourceResourceType})`,
        value: `${adapter.sourceModule}|${adapter.sourceResourceType}`
      }));
  }

  loadTemplateVersion(): void {
    this.selectedVersion = undefined;
    if (!this.selectedTemplateId) {
      return;
    }
    this.loading = true;
    this.auditService.getTemplate(this.selectedTemplateId).subscribe({
      next: response => {
        const template = response.data;
        this.selectedVersion = template?.versions?.find(version => version.status === 'PUBLISHED');
        this.form.templateVersionId = this.selectedVersion?.templateVersionId;
        this.form.moduleCode = this.form.moduleCode || template?.moduleCode;
        this.form.auditType = this.form.auditType || template?.auditType;
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Template', detail: 'Unable to load selected template.' }),
      complete: () => this.loading = false
    });
  }

  createCase(): void {
    this.formError = '';
    if (!this.form.templateVersionId) {
      this.formError = 'Select a published audit template before creating the case.';
      return;
    }
    this.saving = true;
    this.auditService.createCase(this.form).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Case created', detail: response.message || 'Audit case created.' });
        if (response.data?.auditCaseId) {
          this.router.navigate(['/audit/cases', response.data.auditCaseId]);
        }
      },
      error: error => this.formError = error?.error?.message || 'Unable to create audit case.',
      complete: () => this.saving = false
    });
  }

  searchSourceResources(): void {
    const adapter = this.selectedSourceAdapter();
    if (!adapter?.sourceModule || !adapter.sourceResourceType) {
      return;
    }
    this.sourceSearching = true;
    this.auditService.sourceResources(adapter.sourceModule, adapter.sourceResourceType, this.sourceQuery).subscribe({
      next: response => this.sourceResults = response.data ?? [],
      error: () => this.messageService.add({ severity: 'error', summary: 'Source search', detail: 'Unable to search source records.' }),
      complete: () => this.sourceSearching = false
    });
  }

  useSourceResource(source: AuditSourceResource): void {
    this.form.sourceModule = source.sourceModule;
    this.form.sourceResourceType = source.sourceResourceType;
    this.form.sourceResourceId = source.sourceResourceId;
    this.form.sourceResourceNumber = source.sourceResourceNumber;
    this.form.sourceDisplayName = source.sourceDisplayName;
    this.form.scopeType = this.form.scopeType || source.sourceResourceType;
    this.form.scopeId = this.form.scopeId || source.sourceResourceId;
    this.form.scopeNumber = this.form.scopeNumber || source.sourceResourceNumber;
    this.form.scopeDisplayName = this.form.scopeDisplayName || source.sourceDisplayName;
    this.form.moduleCode = this.form.moduleCode || source.sourceModule;
    this.messageService.add({
      severity: 'success',
      summary: 'Source selected',
      detail: source.sourceDisplayName || source.sourceResourceNumber || 'Source selected.'
    });
  }

  private selectedSourceAdapter(): AuditSourceAdapter | undefined {
    const [sourceModule, sourceResourceType] = (this.selectedSourceAdapterKey || '').split('|');
    return this.sourceAdapters.find(adapter => adapter.sourceModule === sourceModule && adapter.sourceResourceType === sourceResourceType);
  }
}
